/**
 * Offline Support Utilities
 * Handles offline detection, data persistence, and sync
 */

import { useEffect, useState, useCallback } from 'react';

/**
 * Online/Offline status detection
 */
export function useOnlineStatus(): boolean {
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return isOnline;
}

/**
 * Service Worker registration
 * Note: ServiceWorker is not needed in Tauri environment
 */
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  // Skip ServiceWorker registration in Tauri environment
  const isTauri = typeof window !== 'undefined' && '__TAURI_INTERNALS__' in window;
  if (isTauri) {
    return null;
  }

  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.register('/sw.js', {
        scope: '/',
      });

      console.log('[SW] Service worker registered:', registration);

      // Check for updates only when page is visible (remember.md: 离开页面不再请求网络)
      setInterval(() => {
        if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
          registration.update();
        }
      }, 60000); // Check every minute

      return registration;
    } catch (error) {
      console.error('[SW] Service worker registration failed:', error);
      return null;
    }
  }
  return null;
}

/**
 * Unregister service worker
 */
export async function unregisterServiceWorker(): Promise<boolean> {
  if ('serviceWorker' in navigator) {
    const registration = await navigator.serviceWorker.getRegistration();
    if (registration) {
      return registration.unregister();
    }
  }
  return false;
}

/**
 * Clear service worker cache
 */
export async function clearServiceWorkerCache(): Promise<void> {
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage({
      type: 'CLEAR_CACHE',
    });
  }
}

/**
 * Local storage with expiration
 */
interface StorageItem<T> {
  value: T;
  expiry: number | null;
}

export class PersistentStorage {
  private prefix: string;

  constructor(prefix = 'app') {
    this.prefix = prefix;
  }

  private getKey(key: string): string {
    return `${this.prefix}:${key}`;
  }

  /**
   * Set item with optional expiration (in milliseconds)
   */
  set<T>(key: string, value: T, ttl?: number): void {
    const item: StorageItem<T> = {
      value,
      expiry: ttl ? Date.now() + ttl : null,
    };

    try {
      localStorage.setItem(this.getKey(key), JSON.stringify(item));
    } catch (error) {
      console.error('[Storage] Failed to set item:', error);
    }
  }

  /**
   * Get item, returns null if expired or not found
   */
  get<T>(key: string): T | null {
    try {
      const itemStr = localStorage.getItem(this.getKey(key));
      if (!itemStr) {
        return null;
      }

      const item: StorageItem<T> = JSON.parse(itemStr);

      // Check expiration
      if (item.expiry && Date.now() > item.expiry) {
        this.remove(key);
        return null;
      }

      return item.value;
    } catch (error) {
      console.error('[Storage] Failed to get item:', error);
      return null;
    }
  }

  /**
   * Remove item
   */
  remove(key: string): void {
    try {
      localStorage.removeItem(this.getKey(key));
    } catch (error) {
      console.error('[Storage] Failed to remove item:', error);
    }
  }

  /**
   * Clear all items with this prefix
   */
  clear(): void {
    try {
      const keys = Object.keys(localStorage);
      keys.forEach((key) => {
        if (key.startsWith(`${this.prefix}:`)) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.error('[Storage] Failed to clear storage:', error);
    }
  }

  /**
   * Get all keys with this prefix
   */
  keys(): string[] {
    try {
      const keys = Object.keys(localStorage);
      return keys
        .filter((key) => key.startsWith(`${this.prefix}:`))
        .map((key) => key.replace(`${this.prefix}:`, ''));
    } catch (error) {
      console.error('[Storage] Failed to get keys:', error);
      return [];
    }
  }
}

/**
 * Sync queue for offline operations
 */
export interface SyncOperation {
  id: string;
  type: string;
  data: any;
  timestamp: number;
  retries: number;
}

export class SyncQueue {
  private storage: PersistentStorage;
  private queueKey = 'sync-queue';
  private maxRetries = 3;

  constructor(storage: PersistentStorage) {
    this.storage = storage;
  }

  /**
   * Add operation to sync queue
   */
  enqueue(type: string, data: any): string {
    const operation: SyncOperation = {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type,
      data,
      timestamp: Date.now(),
      retries: 0,
    };

    const queue = this.getQueue();
    queue.push(operation);
    this.storage.set(this.queueKey, queue);

    return operation.id;
  }

  /**
   * Get all pending operations
   */
  getQueue(): SyncOperation[] {
    return this.storage.get<SyncOperation[]>(this.queueKey) || [];
  }

  /**
   * Remove operation from queue
   */
  dequeue(id: string): void {
    const queue = this.getQueue();
    const filtered = queue.filter((op) => op.id !== id);
    this.storage.set(this.queueKey, filtered);
  }

  /**
   * Increment retry count for operation
   */
  incrementRetry(id: string): void {
    const queue = this.getQueue();
    const operation = queue.find((op) => op.id === id);
    
    if (operation) {
      operation.retries += 1;
      
      if (operation.retries >= this.maxRetries) {
        // Remove if max retries exceeded
        this.dequeue(id);
      } else {
        this.storage.set(this.queueKey, queue);
      }
    }
  }

  /**
   * Clear all operations
   */
  clear(): void {
    this.storage.remove(this.queueKey);
  }

  /**
   * Get queue size
   */
  size(): number {
    return this.getQueue().length;
  }
}

/**
 * Hook for sync queue management
 */
export function useSyncQueue() {
  const [storage] = useState(() => new PersistentStorage('offline'));
  const [syncQueue] = useState(() => new SyncQueue(storage));
  const [queueSize, setQueueSize] = useState(syncQueue.size());
  const isOnline = useOnlineStatus();

  const enqueue = useCallback(
    (type: string, data: any) => {
      const id = syncQueue.enqueue(type, data);
      setQueueSize(syncQueue.size());
      return id;
    },
    [syncQueue]
  );

  const processQueue = useCallback(
    async (processor: (operation: SyncOperation) => Promise<boolean>) => {
      if (!isOnline) {
        return;
      }

      const queue = syncQueue.getQueue();
      
      for (const operation of queue) {
        try {
          const success = await processor(operation);
          
          if (success) {
            syncQueue.dequeue(operation.id);
          } else {
            syncQueue.incrementRetry(operation.id);
          }
        } catch (error) {
          console.error('[Sync] Failed to process operation:', error);
          syncQueue.incrementRetry(operation.id);
        }
      }

      setQueueSize(syncQueue.size());
    },
    [syncQueue, isOnline]
  );

  const clearQueue = useCallback(() => {
    syncQueue.clear();
    setQueueSize(0);
  }, [syncQueue]);

  // Auto-process queue when coming online
  useEffect(() => {
    if (isOnline && queueSize > 0) {
      // Notify that we're back online and have pending operations
      console.log(`[Sync] Back online with ${queueSize} pending operations`);
    }
  }, [isOnline, queueSize]);

  return {
    enqueue,
    processQueue,
    clearQueue,
    queueSize,
    isOnline,
  };
}
